/**
 * The shell's router.
 *
 * React Router is the thing actually driving navigation; this wraps it in the shape the
 * shell's components were written against — `pathname` as a route PATTERN, `query` with
 * path and search params merged, and an `events` emitter. That shape is Next's pages
 * router, kept on purpose rather than out of inertia: RouteGuard, AuthGuard, RouteTracker,
 * ShellNavBridge, EnhancedOverlaySidebar and SimpleLayout all depend on those three
 * properties, and rewriting them to raw React Router hooks would have meant re-deriving
 * the route-pattern matching and the event bus inline in six places.
 *
 * It is ALSO what `src/compat/next-router.tsx` re-exports to federated remotes, so the
 * shell and every remote share one router instance. See src/compat/README.md.
 *
 * Deliberately NOT implemented: `beforePopState`, `isFallback`, `isPreview`, shallow
 * routing and the locale fields. Nothing in the shell or in any remote's exposed module
 * uses them, and a stub that silently no-ops is worse than an absent export — an import
 * would at least fail loudly at build time.
 */

import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useMatches } from 'react-router-dom';
import { routerEvents } from '@/compat/router-events';

export interface ShellRouter {
  /** The ROUTE PATTERN in Next's bracket form, e.g. `/view/[id]` — not the live URL. */
  pathname: string;
  route: string;
  /** The live URL including query and hash, e.g. `/view/abc?preview=true`. */
  asPath: string;
  /** Path params and search params merged, the way Next merges them. */
  query: Record<string, string | string[]>;
  /**
   * Always true.
   *
   * In Next this is false on the first render of an automatically-statically-optimized
   * page, because the query is not known until hydration. This shell renders only in the
   * browser and reads the query straight off the live URL, so it is known on the first
   * render and there is no "not ready" state to represent. Remotes that gate on it
   * (cii-detail, eeoi-detail) therefore proceed immediately, which is the correct
   * behaviour — the value they were waiting for is already there.
   */
  isReady: boolean;
  basePath: string;
  push: (url: string, as?: string, options?: { scroll?: boolean }) => Promise<boolean>;
  replace: (url: string, as?: string, options?: { scroll?: boolean }) => Promise<boolean>;
  back: () => void;
  forward: () => void;
  reload: () => void;
  prefetch: (url: string) => Promise<void>;
  events: typeof routerEvents;
}

/**
 * Next's route pattern for the current match.
 *
 * EnhancedOverlaySidebar compares `router.pathname === '/view/[id]'` to decide whether a
 * sidebar entry is the active one, so the pattern has to come back in Next's bracket
 * form, not React Router's `:id` form and not the resolved URL. Each route carries its
 * Next pattern on `handle.nextPath` (see src/routes/index.tsx); this reads the deepest
 * match that declares one and falls back to the live pathname for routes that do not —
 * which is every static route, where the two are identical anyway.
 */
function useNextPathname(livePathname: string): string {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const handle = matches[i]?.handle as { nextPath?: string } | undefined;
    if (handle?.nextPath) return handle.nextPath;
  }
  return livePathname;
}

export function useShellRouter(): ShellRouter {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const pathname = useNextPathname(location.pathname);

  const query = useMemo(() => {
    const merged: Record<string, string | string[]> = {};

    // Search params first, path params second: where a route has both `/view/[id]` and
    // `?id=`, Next resolves to the path param, and so does this.
    const search = new URLSearchParams(location.search);
    for (const key of Array.from(new Set(search.keys()))) {
      const all = search.getAll(key);
      merged[key] = all.length > 1 ? all : all[0];
    }
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) merged[key] = value;
    }

    return merged;
  }, [location.search, params]);

  const push = useCallback(
    async (url: string) => {
      navigate(url);
      return true;
    },
    [navigate],
  );

  const replace = useCallback(
    async (url: string) => {
      navigate(url, { replace: true });
      return true;
    },
    [navigate],
  );

  return useMemo<ShellRouter>(
    () => ({
      pathname,
      route: pathname,
      asPath: `${location.pathname}${location.search}${location.hash}`,
      query,
      isReady: true,
      basePath: '',
      push,
      replace,
      back: () => navigate(-1),
      forward: () => navigate(1),
      // Deliberately a real reload rather than a re-render. Every caller of this in the
      // Next shell meant "throw away the page state and start over" — the recovery path
      // after a failed remote load — and a soft re-render would keep exactly the broken
      // state they are trying to discard.
      reload: () => window.location.reload(),
      // A no-op, not an omission. Prefetching a federated remote's chunk is the
      // federation runtime's business, not the router's, and there is no route-level
      // payload to warm the way there is for a Next page.
      prefetch: async () => undefined,
      events: routerEvents,
    }),
    [pathname, location.pathname, location.search, location.hash, query, push, replace, navigate],
  );
}
